// Packages Selectors

import forEach from 'lodash-es/forEach';
import get from 'lodash-es/get';
import groupBy from 'lodash-es/groupBy';
import sortBy from 'lodash-es/sortBy';
import { areCabanasAvailable } from '../../../utilities/packagesUtils';
import { RESERVATION_TYPES } from '../ManageReservation/manageReservation.selectors';

/**
 * Get the sum of all selected packages an its quantities.
 * @param {Object} state Packages store
 * @return {number} Net total
 */
export const getPackagesNetTotal = state => {
  const { selectedPackages } = state;
  const packagesNetTotal = selectedPackages.reduce((acc, selectedPackage) => {
    const { total, quantity, isAdjustment } = selectedPackage;
    return !isAdjustment ? total * parseInt(quantity) + acc : acc;
  }, 0);

  return packagesNetTotal;
};

/**
 * Get the sum of all selected packages taxes.
 * @param {Object} state Packages store
 * @return {number} All packages taxes
 */
export const getPackagesTaxes = state => {
  const { selectedPackages } = state;

  return selectedPackages.reduce((acc, selectedPackage) => {
    const { tax, quantity, isAdjustment } = selectedPackage;
    return !isAdjustment ? tax * parseInt(quantity) + acc : acc;
  }, 0);
};

/**
 * This function returns all the packages by type and merges the result with their
 * availability date.
 * @param {Object} state Global state
 * @param {String} type The package type
 * @return {array} List of objects cotaining packages and cabanas data.
 */
export const getPackagesAndCabanasByType = (state, type) => {
  const packages = getPackagesByType(state.packages, type);
  return joinAvailabilityDataToPackages(state, packages);
};

export const getPackagesGrossTotal = state => {
  const { selectedPackages } = state;

  if (!selectedPackages) return 0;

  const packagesGrossTotal = selectedPackages.reduce((acc, selectedPackage) => {
    const { amount, quantity, isAdjustment } = selectedPackage;
    return !isAdjustment ? amount * parseInt(quantity) + acc : acc;
  }, 0);

  return packagesGrossTotal;
};

/**
 * Returns packages availability list.
 * @param {Object} State Packages Store.
 * @return {array} List of availability packages.
 */
export const getPackages = state => get(state, 'packageAvailabilityList');

/**
 * Returns the Birthday packages availability list.
 * @param {Object} State Packages Store.
 * @return {array} List of availability packages.
 */
export const getPackagesByType = (state, packageType) => {
  const { packageAvailabilityList } = state;
  if (!packageAvailabilityList) return [];

  return packageAvailabilityList.filter(pck => pck.packageType?.includes(packageType));
};

/**
 * Returns cabanas availability list.
 * @param {Object} State Packages Store.
 * @return {array} List of availability cabanas.
 */
export const getCabanas = state => get(state, 'cabanasAvailabilityList');

/**
 * Check if cabana is available for the selected dates and suites
 * @param {array} packagesList - available package and cabana list.
 * @return {boolean} - wether to show cabana tile or not.
 */
export const getCabanaAvailability = state => {
  const cabanasAvailabilityList = getCabanas(state);
  return areCabanasAvailable(cabanasAvailabilityList);
};

/**
 * Returns packages availability list.
 * @param {Object} State Packages Store.
 * @return {boolean} True if it is loading.
 */
export const getIsLoading = state => get(state, 'isLoading');

export const getPackagesGroupedByTier = (state, reservationType, isNewReservation) => {
  const packages = getPackages(state);
  const isSuite = reservationType === RESERVATION_TYPES.suite;
  const isDayPass = reservationType === RESERVATION_TYPES.dayPass;
  const isSuiteConfirmation = isSuite && isNewReservation;
  const isDayPassConfirmation = isDayPass && isNewReservation;

  const { '': noTier, None, ...groupedByTier } = groupBy(
    packages,
    ({ tierDayPassConfirmation, tierDaypassReservation, tierMyReservation, tierConfirmation }) => {
      if (isDayPassConfirmation) {
        return tierDayPassConfirmation;
      } else if (isDayPass) {
        return tierDaypassReservation;
      } else if (isSuiteConfirmation) {
        return tierConfirmation;
      }
      return tierMyReservation;
    }
  );

  let result = {};

  forEach(groupedByTier, (groupedPackage, key) => {
    result[key] = sortBy(groupedPackage, gwPackage => parseInt(gwPackage.orderMyReservation));
  });

  return result;
};

export const getPlanPackages = state => get(state, 'planPackages');

/**
 * Returns packages availability list.
 * @param {Object} State Packages Store.
 * @return {boolean} True if request failed
 */
export const getRequestFailed = state => get(state, 'requestFailed');

/**
 * Returns selected packages list.
 * @param {Object} State Packages Store.
 * @return {array} List of availability packages.
 */
export const getSelectedPackages = state => get(state, 'selectedPackages');

/**
 * This function properly join the availability data to the packages and cabanas
 * returning a full detailed list of packages and cabanas.
 * @param {Object} state Global state.
 * @param {Object} packagesAndCabanas List of packages and cabanas whitout the
 * availability information.
 * @return {array} List of packages and cabanas fully detailed with their
 * availability information.
 */
const joinAvailabilityDataToPackages = (state, packagesAndCabanas) => {
  const availabilityPackages = getPackages(state.packages);
  const availabilityCabanas = getCabanas(state.packages);

  return (
    packagesAndCabanas.length > 0 &&
    packagesAndCabanas?.map(pkg => {
      const cabanaAvailable =
        availabilityCabanas?.length > 0 &&
        availabilityCabanas?.filter(avCabana => avCabana.itemCode === pkg.packageCode && !avCabana.errorCode);

      if (cabanaAvailable && cabanaAvailable?.length > 0) {
        return {
          ...pkg,
          isCabana: true,
          cabanasDetails: cabanaAvailable?.map(cabana => {
            return {
              amount: get(cabana, 'roomBaseRate'),
              tax: get(cabana, 'taxesAndFees'),
              total: get(cabana, 'roomRateTotal'),
              errorCode: get(cabana, 'errorCode'),
              statusText: get(cabana, 'statusText'),
              itemAvailable: get(cabana, 'itemAvailable'),
              date: get(cabana, 'date')
            };
          })
        };
      }
      const packageAvailable = availabilityPackages?.find(avPackage => avPackage.packageCode === pkg.packageCode);
      return {
        ...pkg,
        subTitle: get(packageAvailable, 'subTitle', ''),
        amount: get(packageAvailable, 'amount'),
        tax: get(packageAvailable, 'tax'),
        total: get(packageAvailable, 'total')
      };
    })
  );
};

export const getAddedCabanasv3 = ({ addApackage }) => {
  return Object.values(addApackage.addedPackages);
};
